import { computed, effectScope, nextTick, onScopeDispose, ref, watch } from 'vue';
import { defineStore } from 'pinia';
import { breakpointsTailwind, useBreakpoints, useEventListener, useTitle } from '@vueuse/core';
import { useBoolean } from '@sa/hooks';
import { useRoute } from 'vue-router';
import { setupLoading, setupNProgress } from '@/plugins';
import { SetupStoreId } from '@/enum';
import { router } from '@/router';
import { $t, setLocale } from '@/locales';
import { setDayjsLocale } from '@/locales/dayjs';
import { localStg } from '@/utils/storage';
import { fetchGetLanguageList, fetchSetLanguage, fetchGetFirmwareType } from '@/service/api';
import { getPageInfo, updateLocaleOfGlobalMenus } from '@/store/modules/route/shared';
import local from '@/locales/langs/zh-cn';
import localEn from '@/locales/langs/en-us';
import { formatMenuListHome, formatFirmwareTypeList } from '@/utils/index';
import { useRouteStore } from '../route';
import { useTabStore } from '../tab';
import { useThemeStore } from '../theme';
import { useAuthStore } from '../auth';


export const useAppStore = defineStore(SetupStoreId.App, () => {
  const { firmwareTrans } = useAuthStore();
  const themeStore = useThemeStore();
  const routeStore = useRouteStore();
  const tabStore = useTabStore();
  const scope = effectScope();
  const route = useRoute();
  const breakpoints = useBreakpoints(breakpointsTailwind);
  const { bool: themeDrawerVisible, setTrue: openThemeDrawer, setFalse: closeThemeDrawer } = useBoolean();
  const { bool: reloadFlag, setBool: setReloadFlag } = useBoolean(true);
  const { bool: fullContent, toggle: toggleFullContent } = useBoolean();
  const { bool: contentXScrollable, setBool: setContentXScrollable } = useBoolean();
  const { bool: siderCollapse, setBool: setSiderCollapse, toggle: toggleSiderCollapse } = useBoolean(true);
  const {
    bool: mixSiderFixed,
    setBool: setMixSiderFixed,
    toggle: toggleMixSiderFixed
  } = useBoolean(localStg.get('mixSiderFixed') === 'Y');

  /** Is mobile layout */
  const isMobile = breakpoints.smaller('sm');

  /**
   * Reload page
   *
   * @param duration Duration time
   */
  async function reloadPage(duration = 300) {
    setReloadFlag(false);

    const d = themeStore.page.animate ? duration : 40;

    await new Promise(resolve => {
      setTimeout(resolve, d);
    });

    setReloadFlag(true);
  }

  const locale = ref<App.I18n.LangType>(localStg.get('lang') || 'zh_CN');
  const menus = computed(() => routeStore.menus);

  const localeOptions: App.I18n.LangOption[] = [
    {
      label: '简体中文',
      key: 'zh_CN'
    },
    {
      label: 'English',
      key: 'en_US'
    }
  ];
  const { bool: headerFlag, setBool: setHeaderFlag } = useBoolean(true);

  async function changeLocale(lang: App.I18n.LangType) {
    setHeaderFlag(false);
    locale.value = lang;
    setLocale(lang);
    localStg.set('lang', lang);
    const isLogin = Boolean(localStg.get('token'));
    if (isLogin) {
      await fetchSetLanguage({ lang });
    }
    setHeaderFlag(true);
    reloadPage();
  }

  /** Update document title by locale */
  function updateDocumentTitleByLocale() {
    const { i18nKey, title } = router.currentRoute.value.meta;

    const documentTitle = i18nKey ? $t(i18nKey) : title;

    useTitle(documentTitle);
  }


  async function updateLocalString(flag?: string) {
    setHeaderFlag(false);
    const { data } = await fetchGetLanguageList();
    switch (flag) {
      case "menu":
        updateLocalMenu(data)
        break;
      case "firmware":
        updateLocalFirmwareType(data)
        break;
      default:
        updateLocalMenu(data)
        updateLocalFirmwareType(data)
        break;
    }
  }

  // 修改菜单时获取最新的词条以更新左侧菜单栏以及页面标题显示
  function updateLocalMenu(data: any) {
    const { localRouteEn, localRouteCh } = formatMenuListHome(data);
    local.route = { ...local.route, ...localRouteCh };
    localEn.route = { ...localEn.route, ...localRouteEn };
    updateDocumentTitleByLocale();
    updateLocaleOfGlobalMenus(menus.value);
    routeStore.updateGlobalMenusByLocale();
    setHeaderFlag(true);
  }

  // 添加 - 固件类型多语言静态词条处理
  async function updateLocalFirmwareType(data: any) {
    const { data: list } = await fetchGetFirmwareType();

    const { typeList } = formatFirmwareTypeList(data, list);

    Object.assign(firmwareTrans, { typeList });
    setHeaderFlag(true);
  }



  function init() {
    setDayjsLocale(locale.value);
  }

  // watch store
  scope.run(() => {
    // watch isMobile, if is mobile, collapse sider
    watch(
      isMobile,
      newValue => {
        if (newValue) {
          // backup theme setting before is mobile
          localStg.set('backupThemeSettingBeforeIsMobile', {
            layout: themeStore.layout.mode,
            siderCollapse: siderCollapse.value
          });

          themeStore.setThemeLayout('vertical');
          setSiderCollapse(true);
        } else {
          // when is not mobile, recover the backup theme setting
          const backup = localStg.get('backupThemeSettingBeforeIsMobile');

          if (backup) {
            nextTick(() => {
              themeStore.setThemeLayout(backup.layout);
              setSiderCollapse(backup.siderCollapse);

              localStg.remove('backupThemeSettingBeforeIsMobile');
            });
          }
        }
      },
      { immediate: true }
    );

    // watch locale
    watch(locale, () => {
      // update document title by locale
      updateDocumentTitleByLocale();

      // update global menus by locale
      routeStore.updateGlobalMenusByLocale();

      // update tabs by locale
      tabStore.updateTabsByLocale();

      // sey dayjs locale
      setDayjsLocale(locale.value);
    });
  });

  // cache mixSiderFixed
  useEventListener(window, 'beforeunload', () => {
    localStg.set('mixSiderFixed', mixSiderFixed.value ? 'Y' : 'N');
  });

  /** On scope dispose */
  onScopeDispose(() => {
    scope.stop();
  });

  // init
  init();

  return {
    isMobile,
    reloadFlag,
    reloadPage,
    fullContent,
    locale,
    localeOptions,
    changeLocale,
    themeDrawerVisible,
    openThemeDrawer,
    closeThemeDrawer,
    toggleFullContent,
    contentXScrollable,
    setContentXScrollable,
    siderCollapse,
    setSiderCollapse,
    toggleSiderCollapse,
    mixSiderFixed,
    setMixSiderFixed,
    toggleMixSiderFixed,
    updateLocalMenu,
    headerFlag,
    setHeaderFlag,
    updateLocalString
  };
});
